import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { DASH } from '@/globals';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

interface ILiftDashboardApiModelAPI {
  measurementdashboard_id: number;
  name: string;
  description: string;
  campaign_id: number;
  json_data_string: object;
  created_on_utc: string;
  created_by: number;
  modified_on_utc: string;
  modified_by: number;
  uuid: string;
  public_plaintext_password: string;
}

export default class LiftDashboardApiModel extends Validatable {
  id: number;

  name?: string;

  description: string;

  json?: object;

  campaignID?: number;

  createdOnUtc?: string;

  createdBy?: number;

  modifiedOnUtc?: DateTime;

  modifiedBy?: number;

  uuid?: string;

  publicPlaintextPassword: string;

  constructor(
    id = -1,
    name = null,
    description = '',
    campaignID = null,
    createdBy = null,
    createdOnUtc = DateTime.local(),
    json = null,
    modifiedBy = null,
    modifiedOnUtc = DateTime.local(),
    uuid = null,
    publicPlaintextPassword = '',
  ) {
    super();
    this.id = id;
    this.name = name;
    this.description = description;
    this.campaignID = campaignID;
    this.createdBy = createdBy;
    this.createdOnUtc = createdOnUtc;
    this.json = json;
    this.modifiedBy = modifiedBy;
    this.modifiedOnUtc = modifiedOnUtc;
    this.uuid = uuid;
    this.publicPlaintextPassword = publicPlaintextPassword;

    this.apiObject = null;
    this.liftDashboard = null;
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      LiftDashboardApiModel.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      LiftDashboardApiModel.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      LiftDashboardApiModel.entityPropertiesCached = null;
    });
  }

  get originalObject(): LiftDashboardApiModel | null {
    if (this.apiObject !== null) {
      return LiftDashboardApiModel.fromApi(this.apiObject);
    }
    return null;
  }

  get endpoint(): string {
    return `/campaigns/${this.campaignID}/lift-dashboards/${this.id}`;
  }

  get editEndpoint(): string {
    return `${this.endpoint}/edit`;
  }

  get lastModifiedOnFormatted(): string {
    return this.modifiedOnUtc != null
      ? DateTime.fromISO(this.modifiedOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_SHORT)
      : DASH;
  }

  static fromObject(obj) {
    return super.fromObject(
      obj,
      new LiftDashboardApiModel(
        obj.id,
        obj.name,
        obj.description,
        obj.campaignID,
        obj.createdBy,
        obj.createdOnUtc,
        obj.json,
        obj.modifiedOnUtc,
        obj.modifiedBy,
        obj.uuid,
        obj.publicPlaintextPassword,
      ),
    );
  }

  static fromApi(apiObject: ILiftDashboardApiModelAPI): LiftDashboardApiModel {
    return LiftDashboardApiModel.fromObject({
      id: apiObject.measurementdashboard_id,
      name: apiObject.name,
      description: apiObject.description,
      campaignID: apiObject.campaign_id,
      createdBy: apiObject.created_by,
      createdOnUtc: apiObject.created_on_utc,
      json: apiObject.json_data_string,
      modifiedOnUtc: apiObject.modified_on_utc ? DateTime.fromISO(apiObject.modified_on_utc) : undefined,
      modifiedBy: apiObject.modified_by,
      uuid: apiObject.uuid,
      publicPlaintextPassword: apiObject.public_plaintext_password,
    });
  }

  toApi(): ILiftDashboardApiModelAPI {
    return {
      measurementdashboard_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      description: this.description,
      campaign_id: this.campaignID,
      json_data_string: this.json,
      created_on_utc: this.createdOnUtc,
      created_by: this.createdBy,
      modified_on_utc: this.modifiedOnUtc?.toISO(),
      modified_by: this.modifiedBy,
      uuid: this.uuid,
      public_plaintext_password: this.publicPlaintextPassword,
    };
  }

  static validatorCreateRules(): { name: string } {
    return {
      name: 'required',
    };
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  static get entityProperties() {
    if (LiftDashboardApiModel.entityPropertiesCached != null) {
      return LiftDashboardApiModel.entityPropertiesCached;
    }

    LiftDashboardApiModel.entityPropertiesCached = {
      name: new EntityProperty({
        label: capitalize(i18n.global.t('names.name', { n: 1 })),
        property: 'name',
        type: TableColumnTypes.string,
      }),
      description: new EntityProperty({
        label: capitalize(i18n.global.t('names.description', { n: 1 })),
        property: 'description',
        type: TableColumnTypes.string,
      }),
      modifiedOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.lastModifiedOn')),
        property: 'modified_on_utc',
        type: TableColumnTypes.datetime,
      }),
    };

    return LiftDashboardApiModel.entityPropertiesCached;
  }
}
